import React from 'react';
import {
  Layout,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Checkbox,
  Select,
  Radio,
  Button,
  Card,
  Typography,
  Alert,
  Space,
  Grid,
  Upload,
  Collapse,
  Divider,
  App
} from 'antd';
import {
  UploadOutlined,
  InfoCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { API_ORIGIN } from '../../config';
import { formStrings } from './formStrings';
import { parseFormattedText, renderFormattedBlocks, splitParagraphs } from './formText';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Header, Content } = Layout;

export const FORM_FONT_COLOUR = '#FFAC00';

/**
 * Stored layout files are paths on the API; previews pass blob: or data: URLs for unsaved uploads.
 * Images from the API are loaded with crossOrigin="anonymous": the API sends
 * Cross-Origin-Resource-Policy: same-origin, which blocks plain cross-site <img> loads but not
 * CORS ones, and the API allows the portal's origin.
 */
export const formAssetUrl = (path) => {
  if (!path) return null;
  if (/^(https?:|blob:|data:)/.test(path)) return path;
  return `${API_ORIGIN}${path}`;
};

const formatFieldName = (fieldName) => {
  // Convert snake_case to Title Case
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getFieldLabel = (field) => {
  // Check if label should be hidden based on isVisible
  const shouldShowLabel = field.isVisible !== undefined ? field.isVisible : true;
  if (!shouldShowLabel) {
    return undefined;
  }
  // Use display_label if available, otherwise fallback to formatted field_name
  return field.display_label || formatFieldName(field.field_name);
};

// Get label for validation messages (never returns undefined)
const getFieldLabelForValidation = (field, t) => {
  return field.display_label || formatFieldName(field.field_name) || t.thisField;
};

const validateFile = (file, field, t) => {
  // Check extension
  const ext = file.name.split('.').pop().toLowerCase();
  const allowedTypes = field.allowed_file_types?.split(',').map(type => type.trim().replace(/^\./, '')) || [];

  if (allowedTypes.length > 0 && !allowedTypes.includes(ext)) {
    return t.invalidFileType(ext, field.allowed_file_types);
  }

  // Check size
  const sizeMB = file.size / (1024 * 1024);
  const maxSize = field.max_file_size_mb || 10;
  if (sizeMB > maxSize) {
    return t.fileTooLarge(file.name, sizeMB.toFixed(2), maxSize);
  }

  return null; // No error
};

/**
 * The public dynamic form page: header, titles, banner, instructions and the application card.
 * Used by the live page (/client-portal/:slug) and by the admin preview (/form-preview), so the
 * preview is the page itself rather than an imitation.
 *
 * data: { form, layout, fields } as returned by the "complete" endpoint.
 */
const FormPageView = ({
  data,
  form,
  onFinish,
  submitting = false,
  success = false,
  error = null,
  onCloseSuccess,
  onCloseError,
  isFormDisabled = false,
  onLogoClick,
  successMessage,
  successDescription,
}) => {
  const { modal } = App.useApp();
  const screens = Grid.useBreakpoint();
  const isSmall = !screens.md;
  const formFontColour = FORM_FONT_COLOUR;

  const { form: formInfo, layout, fields } = data;
  const t = formStrings(formInfo.language_id);
  const logoSrc = formAssetUrl(layout?.logo);
  const imageSrc = formAssetUrl(layout?.image);

  const renderFormField = (field) => {
    const fieldLabel = getFieldLabel(field);
    const fieldLabelForValidation = getFieldLabelForValidation(field, t);
    // with the title hidden there is no label to name in the placeholder
    const enterPlaceholder = fieldLabel ? t.enter(fieldLabel) : t.enterAnswer;
    const selectPlaceholder = fieldLabel ? t.select(fieldLabel) : t.selectOption;

    // Check if field name contains 'email' (case-insensitive)
    const isEmailField = field.field_name?.toLowerCase().includes('email');

    const rules = [
      {
        required: field.is_required,
        message: t.required(fieldLabelForValidation)
      }
    ];

    // Add email validation if field name contains 'email'
    if (isEmailField) {
      rules.push({
        type: 'email',
        message: t.invalidEmail
      });
    }

    // Pick limits for checkbox fields. An optional question left blank is fine; the required rule covers the rest.
    const picks = field.field_type === 'checkbox' ? t.picks(field.min_selections, field.max_selections) : null;
    if (picks) {
      rules.push({
        validator: (_, value) => {
          const count = Array.isArray(value) ? value.length : 0;
          if (count === 0) return Promise.resolve();
          if (field.min_selections && count < field.min_selections) return Promise.reject(picks);
          if (field.max_selections && count > field.max_selections) return Promise.reject(picks);
          return Promise.resolve();
        }
      });
    }

    // Helper to render field description if it exists
    const renderFieldDescription = () => {
      if (!field.field_description) return null;

      return (
        <div style={{
          marginBottom: '12px',
          lineHeight: '1.6',
          textAlign: 'left'
        }}>
          {renderFormattedBlocks(field.field_description, '14px')}
        </div>
      );
    };

    switch (field.field_type) {
      case 'varchar':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <Input placeholder={enterPlaceholder} />
            </Form.Item>
          </div>
        );

      case 'text':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <TextArea rows={4} placeholder={enterPlaceholder} />
            </Form.Item>
          </div>
        );

      case 'int':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder={enterPlaceholder}
              />
            </Form.Item>
          </div>
        );

      case 'decimal':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <InputNumber
                style={{ width: '100%' }}
                precision={2}
                step={0.01}
                placeholder={enterPlaceholder}
              />
            </Form.Item>
          </div>
        );

      case 'date':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <DatePicker style={{ width: '100%' }} placeholder={t.selectDate} />
            </Form.Item>
          </div>
        );

      case 'boolean':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              valuePropName="checked"
              rules={rules}
            >
            <Checkbox>
              {fieldLabel}
            </Checkbox>
          </Form.Item>
          </div>
        );

      case 'select': {
        if (!field.field_options || field.field_options.length === 0) {
          return (
            <div key={field.field_id} data-field-name={field.field_name}>
              {renderFieldDescription()}
              <Form.Item label={fieldLabel}>
                <Alert type="warning" message={t.noOptions} showIcon />
              </Form.Item>
            </div>
          );
        }

        const selectOptions = [...field.field_options.map(opt => {
          const capacity = field.workshop_capacity?.[opt];
          let label = opt;
          let disabled = false;

          if (capacity) {
            if (capacity.is_full) {
              label = `${opt} (${t.full})`;
              disabled = true;
            }
          }

          return { label, value: opt, disabled };
        })];

        if (field.allow_other === 1 || field.allow_other === true) {
          const otherLabel = field.other_option_label || t.other;
          selectOptions.push({ label: otherLabel, value: '__other__' });
        }

        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <Select
                placeholder={selectPlaceholder}
                options={selectOptions}
              />
            </Form.Item>
            {(field.allow_other === 1 || field.allow_other === true) && (
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                prevValues[field.field_name] !== currentValues[field.field_name]
              }>
                {({ getFieldValue }) =>
                  getFieldValue(field.field_name) === '__other__' ? (
                    <Form.Item
                      name={`${field.field_name}_other_text`}
                      label={t.pleaseSpecify}
                      rules={[{ required: field.is_required, message: t.specifyAnswer }]}
                      style={{ marginTop: -16 }}
                    >
                      <Input placeholder={t.enterAnswer} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            )}
          </div>
        );
      }

      case 'radio': {
        if (!field.field_options || field.field_options.length === 0) {
          return (
            <div key={field.field_id} data-field-name={field.field_name}>
              {renderFieldDescription()}
              <Form.Item label={fieldLabel}>
                <Alert type="warning" message={t.noOptions} showIcon />
              </Form.Item>
            </div>
          );
        }

        const isWorkshopField = field.workshop_capacity && Object.keys(field.workshop_capacity).length > 0;

        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              rules={rules}
            >
              <Radio.Group
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ display: 'flex', alignItems: 'flex-start', gap: isWorkshopField ? '16px' : '8px' }}>
                  {field.field_options.map((opt, idx) => {
                    const capacity = field.workshop_capacity?.[opt];
                    let label = opt;
                    let disabled = false;

                    if (capacity) {
                      if (capacity.is_full) {
                        label = `${opt} (${t.full})`;
                        disabled = true;
                      }
                    }

                    return (
                      <div
                        key={idx}
                        style={isWorkshopField ? {
                          paddingBottom: '12px',
                          borderBottom: idx < field.field_options.length - 1 ? '1px solid #f0f0f0' : 'none',
                          width: '100%'
                        } : {}}
                      >
                        <Radio value={opt} disabled={disabled}>{label}</Radio>
                      </div>
                    );
                  })}
                  {(field.allow_other === 1 || field.allow_other === true) && (
                    <Radio value="__other__">{field.other_option_label || t.other}</Radio>
                  )}
                </Space>
              </Radio.Group>
            </Form.Item>
            {(field.allow_other === 1 || field.allow_other === true) && (
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                prevValues[field.field_name] !== currentValues[field.field_name]
              }>
                {({ getFieldValue }) =>
                  getFieldValue(field.field_name) === '__other__' ? (
                    <Form.Item
                      name={`${field.field_name}_other_text`}
                      label={t.pleaseSpecify}
                      rules={[{ required: field.is_required, message: t.specifyAnswer }]}
                      style={{ marginTop: -16, marginLeft: 24 }}
                    >
                      <Input placeholder={t.enterAnswer} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            )}
          </div>
        );
      }

      case 'checkbox': {
        if (!field.field_options || field.field_options.length === 0) {
          return (
            <div key={field.field_id} data-field-name={field.field_name}>
              {renderFieldDescription()}
              <Form.Item label={fieldLabel}>
                <Alert type="warning" message={t.noOptions} showIcon />
              </Form.Item>
            </div>
          );
        }

        const isWorkshopCheckbox = field.workshop_capacity && Object.keys(field.workshop_capacity).length > 0;

        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            {renderFieldDescription()}
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
              prevValues[field.field_name] !== currentValues[field.field_name]
            }>
              {({ getFieldValue }) => {
                const currentChecked = getFieldValue(field.field_name) || [];
                const atLimit = field.max_selections && currentChecked.length >= field.max_selections;

                return (
                  <Form.Item
                    name={field.field_name}
                    label={fieldLabel}
                    rules={rules}
                    extra={picks}
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Space direction="vertical" style={{ display: 'flex', alignItems: 'flex-start', gap: isWorkshopCheckbox ? '16px' : '8px' }}>
                        {field.field_options.map((opt, idx) => {
                          const capacity = field.workshop_capacity?.[opt];
                          let label = opt;
                          let disabled = false;

                          if (capacity?.is_full) {
                            label = `${opt} (${t.full})`;
                            disabled = true;
                          }

                          // Disable unchecked options when max is reached
                          if (atLimit && !currentChecked.includes(opt)) {
                            disabled = true;
                          }

                          return (
                            <div
                              key={idx}
                              style={isWorkshopCheckbox ? {
                                paddingBottom: '12px',
                                borderBottom: idx < field.field_options.length - 1 ? '1px solid #f0f0f0' : 'none',
                                width: '100%'
                              } : {}}
                            >
                              <Checkbox value={opt} disabled={disabled}>{label}</Checkbox>
                            </div>
                          );
                        })}
                        {(field.allow_other === 1 || field.allow_other === true) && (
                          <Checkbox
                            value="__other__"
                            disabled={atLimit && !currentChecked.includes('__other__')}
                          >
                            {field.other_option_label || t.other}
                          </Checkbox>
                        )}
                      </Space>
                    </Checkbox.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
            {(field.allow_other === 1 || field.allow_other === true) && (
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
                prevValues[field.field_name] !== currentValues[field.field_name]
              }>
                {({ getFieldValue }) => {
                  const fieldValue = getFieldValue(field.field_name);
                  return fieldValue && fieldValue.includes('__other__') ? (
                    <Form.Item
                      name={`${field.field_name}_other_text`}
                      label={t.pleaseSpecify}
                      rules={[{ required: field.is_required, message: t.specifyAnswer }]}
                      style={{ marginTop: -16, marginLeft: 24 }}
                    >
                      <Input placeholder={t.enterAnswer} />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            )}
          </div>
        );
      }

      case 'file':
        return (
          <div key={field.field_id} data-field-name={field.field_name}>
            <Divider style={{ borderColor: '#d9d9d9', margin: '32px 0 24px 0' }} />
            {renderFieldDescription()}
            <Form.Item
              name={field.field_name}
              label={fieldLabel}
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
              rules={[
                {
                  validator: (_, fileList) => {
                    // Check if file is uploaded
                    if (!fileList || fileList.length === 0) {
                      return field.is_required
                        ? Promise.reject(t.required(fieldLabelForValidation))
                        : Promise.resolve();
                    }

                  // Validate file type and size
                  const file = fileList[0].originFileObj || fileList[0];
                  const error = validateFile(file, field, t);

                  if (error) {
                    return Promise.reject(error);
                  }

                  return Promise.resolve();
                }
              }
            ]}
          >
            <Upload
              maxCount={1}
              beforeUpload={(file) => {

                // Validate immediately on selection
                const error = validateFile(file, field, t);

                if (error) {
                  // Show error modal using App hook
                  modal.error({
                    title: t.invalidFile,
                    content: error,
                    centered: true,
                  });

                  return Upload.LIST_IGNORE; // Prevent file from being added to list
                }
                // Allow file to be added to list but don't upload
                return false;
              }}
              onChange={(info) => {
                // This helps ensure the form knows about file changes
                if (info.fileList.length > 0) {
                  form.setFieldValue(field.field_name, info.fileList);
                } else {
                  // Clear field value when file is removed
                  form.setFieldValue(field.field_name, undefined);
                }
              }}
              onRemove={() => {
                // Clear the field value when file is removed
                form.setFieldValue(field.field_name, undefined);
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>{t.selectFile}</Button>
            </Upload>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
              {t.fileLimits(field.allowed_file_types, field.max_file_size_mb || 10)}
            </Text>
          </Form.Item>
          <Divider style={{ borderColor: '#d9d9d9', margin: '24px 0 32px 0' }} />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        /* Smooth transitions for buttons */
        .ant-btn {
          transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease;
        }

        /* Primary submit button base style */
        .dynamic-form-primary {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(0);
          box-shadow: 0 6px 18px rgba(255,172,0,0.12);
        }

        /* Primary submit hover animation */
        .dynamic-form-primary:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 14px 36px rgba(255,172,0,0.22) !important;
        }

        /* Disabled state */
        .dynamic-form-primary:disabled {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          transform: translateY(0) !important;
          box-shadow: none !important;
        }

        /* Styled info alert */
        .ant-alert-info {
          background-color: #fff8e6 !important;
          border: 1px solid #ffd666 !important;
        }

        .ant-alert-info .ant-alert-icon {
          color: ${formFontColour} !important;
        }
      `}</style>
      <Layout style={{ minHeight: '100vh', background: '#fafafa', overflowX: 'hidden' }}>
      <Header style={{
        padding: '12px 16px',
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        height: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{
            flex: '0 0 auto',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: isSmall ? 8 : 0
          }}>
            <img
              src="/images/FM_Logo.png"
              alt="Festivals Malta"
              onClick={onLogoClick}
              style={{
                height: isSmall ? '32px' : '36px',
                width: 'auto',
                maxWidth: '160px',
                cursor: 'pointer',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </Header>

      <Content style={{ padding: 'clamp(20px, 5vw, 48px)', overflowX: 'hidden' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: isSmall ? '0 12px' : '0 20px', width: '100%', boxSizing: 'border-box' }}>
          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 48px)' }}>
            {logoSrc && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={logoSrc}
                  crossOrigin="anonymous"
                  alt="Logo"
                  style={{
                    height: isSmall ? '100px' : 'clamp(140px, 22vw, 220px)',
                    maxWidth: '100%',
                    maxHeight: '240px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            {layout?.title1 && (
              <Title level={2} style={{ color: '#222', letterSpacing: '0.5px', marginBottom: '8px', fontSize: 'clamp(22px, 4.5vw, 30px)', marginTop: '24px' }}>
                {layout.title1}
              </Title>
            )}
            {layout?.title2 && (
              <Title level={1} style={{ color: formFontColour, marginBottom: '8px', fontSize: 'clamp(32px, 6.5vw, 48px)', fontWeight: 'bold' }}>
                {layout.title2}
              </Title>
            )}
            {layout?.title3 && (
              <Paragraph style={{ fontSize: 'clamp(16px, 3.5vw, 20px)', marginBottom: 0 }}>
                {layout.title3}
              </Paragraph>
            )}
          </div>

          {/* Banner Image */}
          {imageSrc && (
            <Card style={{ marginBottom: '24px', textAlign: 'center' }}>
              <img
                src={imageSrc}
                crossOrigin="anonymous"
                alt={layout.image_desc || 'Banner'}
                style={{
                  maxWidth: '100%',
                  borderRadius: '8px',
                  maxHeight: '400px'
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {layout.image_desc && (
                <div style={{ marginTop: 12 }}>
                  {splitParagraphs(layout.image_desc).map((paragraph, index) => (
                    <Text
                      key={index}
                      style={{
                        display: 'block',
                        marginTop: index > 0 ? '1em' : 0,
                        fontSize: '15px'
                      }}
                    >
                      {parseFormattedText(paragraph.trim())}
                    </Text>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              message={successMessage || t.submitted}
              description={successDescription || t.submittedDetail}
              type="success"
              style={{ marginBottom: '24px' }}
              closable
              onClose={onCloseSuccess}
            />
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              message={error}
              type="error"
              style={{ marginBottom: '24px' }}
              closable
              onClose={onCloseError}
            />
          )}

          {/* Inactive Form Alert */}
          {isFormDisabled && (
            <Alert
              message={t.unavailable}
              description={t.unavailableDetail}
              type="warning"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Form Details & Downloadable File */}
          {(layout?.form_details || layout?.downloadable_file) && (
            <Collapse
              defaultActiveKey={['1']}
              style={{
                marginBottom: '24px',
                backgroundColor: '#fff8e6',
                border: '1px solid #ffd666',
                borderRadius: '8px'
              }}
              items={[
                {
                  key: '1',
                  label: layout?.form_details_title ? (
                    <Space>
                      <InfoCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                      <Text strong style={{ fontSize: '16px' }}>{layout.form_details_title}</Text>
                    </Space>
                  ) : null,
                  showArrow: true,
                  children: (
                    <div>
                      {layout?.form_details && (
                        <div style={{ lineHeight: '1.8', textAlign: layout?.form_details_alignment || 'left' }}>
                          {renderFormattedBlocks(layout.form_details, '16px')}
                        </div>
                      )}

                      {/* Downloadable File inside form details */}
                      {layout?.downloadable_file && (
                        <div style={{ marginTop: layout?.form_details ? '24px' : '0', textAlign: 'left' }}>
                          {layout?.downloadable_file_title && (
                            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px', color: '#000' }}>
                              {layout.downloadable_file_title}
                            </Text>
                          )}
                          <Button
                            type="default"
                            icon={<DownloadOutlined />}
                            href={/^(blob:|data:)/.test(layout.downloadable_file) ? layout.downloadable_file : `${API_ORIGIN}${layout.downloadable_file}`}
                            download
                            target="_blank"
                            size="large"
                            style={{
                              backgroundColor: '#FFAC00',
                              borderColor: '#FFAC00',
                              color: '#fff'
                            }}
                          >
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                }
              ]}
            />
          )}

          {/* Application Form */}
          <Card
            title={
              <span style={{ color: formFontColour, fontSize: '18px', fontWeight: '600' }}>
                {formInfo.application_text || t.application}
              </span>
            }
            style={{
              borderRadius: isSmall ? '8px' : '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0'
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              disabled={isFormDisabled}
            >
              {[...fields]
                .sort((a, b) => a.field_order - b.field_order)
                .map(field => renderFormField(field))}

              <Form.Item style={{ marginTop: '24px', marginBottom: 0 }}>
                <Button
                  className="dynamic-form-primary"
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={isFormDisabled}
                  block
                  size="large"
                  style={{
                    height: '56px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '8px'
                  }}
                >
                  {formInfo.submit_text || t.submit}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </Content>
    </Layout>
    </>
  );
};

export default FormPageView;
